import "@css/Fireworks.css";
import { useRef, useEffect, useImperativeHandle, forwardRef } from "react";

const Fireworks = forwardRef((props, ref) => {
    const canvasRef = useRef(null);
    const fireworks = useRef([]);
    const animationId = useRef(null);
    const width = useRef(window.innerWidth);
    const height = useRef(window.innerHeight);

    useImperativeHandle(ref, () => ({
        launch(
            x = Math.random() * width.current,
            y = (Math.random() * height.current) / 2,
        ) {
            fireworks.current.push(new Firework(x, y));
        },
    }));

    class Particle {
        constructor(x, y, color) {
            this.x = x;
            this.y = y;
            this.color = color;
            this.radius = Math.random() * 2 + 1;
            this.speed = Math.random() * 5 + 1;
            this.angle = Math.random() * 2 * Math.PI;
            this.gravity = 0.05;
            this.alpha = 1;
            this.decay = Math.random() * 0.01 + 0.005;
        }
        update() {
            this.speed *= 0.95;
            this.x += Math.cos(this.angle) * this.speed;
            this.y += Math.sin(this.angle) * this.speed + this.gravity;
            this.alpha -= this.decay;
        }
        draw(ctx) {
            ctx.save();
            ctx.globalAlpha = this.alpha;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.radius, 0, 2 * Math.PI);
            ctx.fillStyle = this.color;
            ctx.fill();
            ctx.restore();
        }
    }

    class Firework {
        constructor(x, y) {
            this.x = x;
            this.y = height.current;
            this.targetY = y; // Высота взрыва передаётся из launch()
            this.speed = Math.random() * 7 + 5;
            this.particles = [];
            this.color = `hsl(${Math.random() * 360}, 100%, 50%)`;
            this.exploded = false;
        }
        update() {
            if (!this.exploded) {
                this.y -= this.speed;
                if (this.y <= this.targetY) {
                    this.exploded = true;
                    for (let i = 0; i < 50; i++) {
                        this.particles.push(
                            new Particle(this.x, this.y, this.color),
                        );
                    }
                }
            } else {
                this.particles.forEach(p => p.update());
                this.particles = this.particles.filter(p => p.alpha > 0);
            }
        }
        draw(ctx) {
            if (!this.exploded) {
                ctx.beginPath();
                ctx.arc(this.x, this.y, 3, 0, 2 * Math.PI);
                ctx.fillStyle = this.color;
                ctx.fill();
            } else {
                this.particles.forEach(p => p.draw(ctx));
            }
        }
        isDone() {
            return this.exploded && this.particles.length === 0;
        }
    }

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        function resize() {
            width.current = window.innerWidth;
            height.current = window.innerHeight;
            canvas.width = width.current;
            canvas.height = height.current;
        }
        resize();
        window.addEventListener("resize", resize);

        function animate() {
            ctx.clearRect(0, 0, width.current, height.current);
            fireworks.current.forEach((fw, i) => {
                fw.update();
                fw.draw(ctx);
                if (fw.isDone()) {
                    fireworks.current.splice(i, 1);
                }
            });
            animationId.current = requestAnimationFrame(animate);
        }

        animate();

        return () => {
            cancelAnimationFrame(animationId.current);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fireworks-canvas"
        />
    );
});

export default Fireworks;
